/**
 * Typed wrapper around the vendored tar1090 marker code.
 *
 * `vendor/tar1090Shapes.js` is the upstream tar1090/ADSB-Exchange icon
 * set (shapes + selector + SVG renderer) verbatim. We keep it unmodified
 * so it can be refreshed from upstream cleanly. This file gives the rest
 * of v2 a small, typed surface so consumers don't reach into the JS
 * module directly.
 *
 * Inputs we have available from MQTT and ADSB Exchange:
 *   - category   (`A1` … `A7`, `B1` … `B6`, `C0` … `C3`)
 *   - type code  (`A343`, `B738`, `EC35`, …) — populated by ADSB
 *                 Exchange's DB; sometimes null for MQTT-only contacts
 *   - altitude   (used by tar1090 only to special-case ground vehicles)
 *
 * Inputs tar1090's selector also accepts but we don't have:
 *   typeDescription, wtc, addrtype, eastbound
 *
 * Passing nulls for those falls through tar1090's chain to the next
 * available lookup — which is exactly the intended fallback path.
 */

// The vendor module has `@ts-nocheck` so we describe its surface here.
// Suppress the implicit-any import.
// @ts-expect-error - vendored JS, no .d.ts
import * as tar1090 from './vendor/tar1090Shapes.js';

interface Tar1090Shape {
  w: number;
  h: number;
  viewBox: string;
  strokeScale?: number;
  path?: string | string[];
  accent?: string | string[];
  accentMult?: number;
  noRotate?: boolean;
  noAspect?: boolean;
  transform?: string;
  svg?: string;
}

type GetBaseMarkerResult = [string, number];

const shapes = tar1090.shapes as Record<string, Tar1090Shape>;
const getBaseMarker = tar1090.getBaseMarker as (
  category: string | null,
  typeDesignator: string | null,
  typeDescription: string | null,
  wtc: string | null,
  addrtype: string | null,
  altitude: number | string | null,
  eastbound: boolean | null,
) => GetBaseMarkerResult;
const svgShapeToSVG = tar1090.svgShapeToSVG as (
  shape: Tar1090Shape,
  fillColor: string,
  strokeColor: string,
  strokeWidth: number,
  scale?: number,
) => string;

/** Pick the tar1090 shape entry for an aircraft. Returns the shape
 *  object + the per-type scale multiplier the selector recommends. */
export function pickAircraftShape(args: {
  category: string | null;
  type: string | null;
  altitudeFt: number | null;
}): { shape: Tar1090Shape; scale: number; shapeName: string } {
  // tar1090 treats `altitude === 'ground'` as a hint for surface
  // vehicles. ADSB Exchange uses the literal string "ground" for
  // alt_baro on ground vehicles; we mirror that.
  const altParam: number | string | null = args.altitudeFt == null
    ? null
    : args.altitudeFt <= 0 ? 'ground' : args.altitudeFt;
  const [shapeName, scale] = getBaseMarker(
    args.category, args.type, null, null, null, altParam, null,
  );
  // Fallback chain — `unknown` is tar1090's "no idea" shape; `airliner`
  // is the second-most-generic. If the lookup whiffs entirely (shouldn't
  // happen because getBaseMarker always returns a valid name) fall back
  // to the first shape in the table just so we always render something.
  const shape = shapes[shapeName] ?? shapes['unknown'] ?? shapes['airliner']
    ?? Object.values(shapes)[0]!;
  return { shape, scale, shapeName };
}

/** Render a complete SVG string for an aircraft marker using tar1090's
 *  own renderer. The string contains a fully-formed `<svg …>…</svg>`,
 *  rotation must be applied externally (e.g. via a CSS transform on
 *  the wrapper). Returns `noRotate` so the caller can skip rotation
 *  for balloons / ground vehicles. */
export function renderAircraftMarker(args: {
  category: string | null;
  type: string | null;
  altitudeFt: number | null;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  /** Extra scale on top of tar1090's per-shape scale. Marker layer
   *  uses this to size all markers similarly (tar1090's intrinsic sizes
   *  vary wildly across shapes). */
  scale?: number;
}): { svg: string; noRotate: boolean; width: number; height: number } {
  const { shape, scale } = pickAircraftShape({
    category: args.category, type: args.type, altitudeFt: args.altitudeFt,
  });
  const finalScale = (args.scale ?? 1) * scale;
  const stroke = args.stroke ?? '#1f2937';
  const strokeWidth = args.strokeWidth ?? 0.4;
  const svg = svgShapeToSVG(shape, args.fill, stroke, strokeWidth, finalScale);
  return {
    svg,
    noRotate: !!shape.noRotate,
    width: shape.w * finalScale,
    height: shape.h * finalScale,
  };
}
